import { NoDataComponent, CustomButton, CustomSelect, CustomTooltip } from '@/shared/ui/components';
import {
  ArtifactIcon,
  InfoIcon,
  arrowDownGreyIcon,
  arrowDownWhiteIcon,
  loadingGreyIcon,
  loadingWhiteIcon,
} from '@/shared/ui/assets';
import {
  ARTIFACT_REPORT_HOVER_TEXT,
  DOUBLE_HYPHEN_BULLET,
  STATUS_REPORT,
  STATUS_REPORT_OPTIONS,
} from '@/shared/config';
import { dateToDuration, dateToTimeSince, gettextStudyType } from '@/shared/lib';
import { Spin } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { forwardRef, memo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { checkNewNotiEventReport } from '@/pages/inbox-details/@x/inbox-list';
import InboxItemSkeleton from './inboxItemSkeleton/inboxItemSkeleton';
import InboxSearchComponent from '../inboxSearchComponent/inboxSearchComponent';
import { generateLabels } from '../../lib/inboxList';
import { useInboxList } from './useInboxList';
import './style.scss';
import type { InboxListProps } from './inboxList.types';

/**
 * Renders one type-filtered inbox list: the search header plus a page of report rows with patient name, generated labels, artifact tooltip, status select, physician, date of service, study type and findings. Renders only while the tab is active or data has loaded, slices the reports by pageIndex and sizePerPage, shows skeleton rows until isLoadedData, and falls back to the no-data placeholder when the current filter matches nothing.
 *
 * @returns The inbox list element, or a falsy value while the tab is inactive and no data has loaded.
 */
const InboxList = forwardRef((props: InboxListProps, ref) => {
  const {
    state,
    filterObject,
    sizePerPage,
    onChangeSearchComponent,
    navigatePage,
    onClickReport,
    onClickStopProg,
    onChange,
  } = useInboxList(props, ref);

  /**
   * Picks the suffix icon for an inbox row's report-status select. A pending change found in state.listChangingStatusReport renders a spinning white loader when the requested status is Completed and a grey one otherwise, while a settled row renders the white chevron for Completed reports and the grey chevron for the rest.
   *
   * @param report - Inbox row whose id and statusReport decide the icon.
   * @returns The spinner or chevron element used as the select's suffix icon.
   */
  const getSuffixIcon = (report: any) => {
    /**
     * Finds the pending status change tracked for an inbox row. Looks the report id up in state.listChangingStatusReport to learn whether a save is in flight and which status was requested.
     *
     * @returns The matching pending status-change entry, or undefined when the row has none.
     */
    const updatingReport = _.find(state.listChangingStatusReport, (item) => item.id === report.id);
    if (updatingReport?.isChangingStatus) {
      if (updatingReport.status === STATUS_REPORT.COMPLETED) {
        return (
          <Spin
            indicator={
              <img src={loadingWhiteIcon} alt="" style={{ animation: 'spin 1s linear infinite' }} />
            }
          />
        );
      }
      return (
        <Spin
          indicator={
            <img src={loadingGreyIcon} alt="" style={{ animation: 'spin 1s linear infinite' }} />
          }
        />
      );
    }
    if (report.statusReport === STATUS_REPORT.COMPLETED) {
      return <img src={arrowDownWhiteIcon} alt="" />;
    }
    return <img src={arrowDownGreyIcon} alt="" />;
  };

  return (
    (props.isActive || state.isLoadedData) && (
      <div className="inbox-list">
        {(props.isActive || state.isLoadedData) && (
          <InboxSearchComponent
            type={props.type}
            isLoadedData={state.isLoadedData}
            inboxes={state.inboxes}
            pageIndex={state.pageIndex}
            isEndOfData={state.isEndOfData}
            totalInboxCount={props.totalInboxCount}
            onChangeSearchComponent={onChangeSearchComponent}
            navigatePage={navigatePage}
            // filterObject={filterObject.current}
            searchGoto={props.searchGoto}
            updateSearchGoto={props.updateSearchGoto}
            selectedFacility={props.selectedFacility}
            selectedTypeSeePatients={props.selectedTypeSeePatients}
          />
        )}
        {props.isActive && (
          <div
            id="inbox-list-body"
            className={classNames('inbox-list__body', !state.isLoadedData ? '--skeleton' : '')}
          >
            {state.inboxes.length || (!state.inboxes.length && !state.isLoadedData) ? (
              _.map(
                state.isLoadedData
                  ? state.inboxes.slice(
                      state.pageIndex * sizePerPage.current,
                      (state.pageIndex + 1) * sizePerPage.current,
                    )
                  : state.skeletonInboxes,
                (report, index) =>
                  state.isLoadedData ? (
                    <div
                      className={classNames(
                        'inbox-item',
                        state.isLoadedData && !report.isRead ? '--new' : '',
                      )}
                      onClick={() => {
                        onClickReport(report);
                      }}
                    >
                      <div className="inbox-item__top-container">
                        <div className="left-content-container">
                          <span className="patient-name">{report.patientName}</span>
                        </div>

                        <div className="right-content-container">
                          <div className="info-container">
                            {_.map(
                              generateLabels(report, props.toggleDisplayDrawer, props.type),
                              (label, index) =>
                                label.isTextButton ? (
                                  <CustomButton
                                    id={`view-details-btn-${report.id}`}
                                    type="secondary"
                                    className="study-id-button --margin"
                                    tooltipTitle={t`View details`}
                                    tooltipClassName="tooltip-study-id"
                                    tooltipPlacement="bottom"
                                    size="small"
                                    icon={<ReactSVG src={InfoIcon} />}
                                    isRightIcon
                                    onClick={label.handleClick}
                                  >
                                    {label.text}
                                  </CustomButton>
                                ) : (
                                  <div
                                    key={index}
                                    className={classNames('label', '--margin', label.className)}
                                  >
                                    {label.text}
                                  </div>
                                ),
                            )}
                          </div>

                          <div className="icon-container">
                            {report.isArtifactReport && (
                              <CustomTooltip
                                placement="bottomRight"
                                title={ARTIFACT_REPORT_HOVER_TEXT}
                              >
                                <img src={ArtifactIcon} alt="Artifact Icon" />
                              </CustomTooltip>
                            )}

                            <CustomSelect
                              className={classNames(
                                report.statusReport === STATUS_REPORT.COMPLETED
                                  ? report.isSavingStatus
                                    ? 'bg-completed__saving'
                                    : 'bg-completed'
                                  : report.isSavingStatus
                                    ? 'status-report__saving'
                                    : 'status-report',
                              )}
                              name="status-report"
                              id={`status-report-select-${report.id}`}
                              onChange={(_name, value) => onChange(value, report.id)}
                              value={report.statusReport}
                              disabled={report.isSavingStatus}
                              options={STATUS_REPORT_OPTIONS}
                              size="middle"
                              showSearch={false}
                              allowClear={false}
                              placement="bottomRight"
                              onClick={onClickStopProg}
                              suffixIcon={getSuffixIcon(report)}
                              aria-label={t`Status report`}
                            />
                          </div>
                        </div>
                      </div>

                      {state.isShowFacilityName && (
                        <div className="inbox-item__facility-container">{report.facilityName}</div>
                      )}

                      <div className="inbox-item__bottom-container">
                        <div className="left-container">
                          <div className="info-item patient-id">
                            <div className="title">{`${t`Patient ID`}:`}</div>
                            <div className="content">
                              <CustomButton
                                id="drawer-btn"
                                className="content button-content"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.toggleDisplayDrawer(report, t`Patient information`);
                                }}
                                style="danger"
                                type="tertiary"
                                size="small"
                              >
                                {report.patientId}
                              </CustomButton>
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Physician`}:`}</div>
                            <div className="content">{report.interprettingPhysicianName}</div>
                          </div>
                          <div className="info-item">
                            <div className="title">
                              {report.type === 'Event'
                                ? `${t`Event Time`}:`
                                : `${t`Date of service`}:`}
                            </div>
                            <div className="content">
                              {report.type === 'Event' ? report.eventTime : report.dateOfService}
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Study type`}:`}</div>
                            <div className="content">{gettextStudyType(report.studyType)}</div>
                          </div>
                        </div>

                        <div className="right-container">
                          <div className="border-left-container">
                            <div className="technician-comments__title">
                              {report.isNewFormat || checkNewNotiEventReport(report)
                                ? t`Findings`
                                : t`Technician comments`}
                            </div>
                            <CustomTooltip
                              classNames={{ root: 'technician-comments__tooltip' }}
                              placement="bottomLeft"
                              mouseLeaveDelay={0}
                              mouseEnterDelay={0}
                              arrow={false}
                              title={report.technicianComments}
                            >
                              <div
                                id={`technician-comments-${report.id}`}
                                className="technician-comments__content"
                              >
                                {report.technicianComments || DOUBLE_HYPHEN_BULLET}
                              </div>
                            </CustomTooltip>
                          </div>
                          <div className="time justify-end">
                            <div>{dateToTimeSince(dateToDuration(report.date))}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <InboxItemSkeleton key={index} />
                  ),
              )
            ) : (
              <NoDataComponent
                isSearching={
                  !!filterObject.current.patientName ||
                  !!filterObject.current.studyFid ||
                  !!filterObject.current.patientId ||
                  !!filterObject.current.physicianId
                }
              />
            )}
          </div>
        )}
      </div>
    )
  );
});

export default memo(InboxList);
